import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import Api from '../utils/Api';
import { useAuth } from './AuthContext';
import { useError } from './ErrorContext';
import { useLoading } from './LoadingContext';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useRoutePath } from '../utils/getRoutePath';

const JobContext = createContext();
 
export const useJobs = () => {
  const context = useContext(JobContext);
  if (!context) {
    throw new Error('useJobs must be used within a JobProvider');
  }
  return context;
};

export const JobProvider = ({ children }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { hideLoading } = useLoading();
  const { getToken } = useAuth();
  const [ setApi] = useState(null);
  const { handleApiError, formErrors, clearErrors } = useError();
  const [jobs, setJobs] = useState([]);
  const segments = location.pathname.split('/');
  const id = segments.pop();
  const [paginate, setPaginate] = useState(true);
  const getRoutePath = useRoutePath();
  const initialized = location.pathname === getRoutePath('/jobs');

  const getJobs = useCallback(async (page = 1, filters = {}) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
   
    try {
      const searchParams = new URLSearchParams(window.location.search);
      let query = '';
      if(filters?.q){
        query = '&q='+filters?.q;
        
      } else{
        const q = searchParams.get('q') || '';
        query = '&q='+q;
      }
      if(filters?.active){
        query += '&active='+filters?.active;
      } else{
        const active = searchParams.get('active') || '';
        query += '&active='+active;
      }

      if(filters?.is_invoiced){
        query += '&is_invoiced='+filters?.is_invoiced;
      } else{
        const is_invoiced = searchParams.get('is_invoiced') || '';
        query += '&is_invoiced='+is_invoiced;
      }
       
      let response = await apiInstance.call('jobs?page=' + page+'&paginate='+paginate +''+query , 'GET');
      if(filters?.all){
        query = '';
        setPaginate(false)
        if(filters?.active){
          query += '&active='+filters?.active;
        } else{
          const active = searchParams.get('active') || '';
          query += '&active='+active;
        }
        if(filters?.is_invoiced){
          query += '&is_invoiced='+filters?.is_invoiced;
        } else{
          const is_invoiced = searchParams.get('is_invoiced') || '';
          query += '&is_invoiced='+is_invoiced;
        }
        response = await apiInstance.call('jobs?page=' + 0 +''+query);  
      }
      
      if (response.status === 200) {
        setPaginate(response.data.paginate)
        setJobs(response.data.jobs || []);
       
        
        if(filters){
          return response.data.jobs;
        }
      } else {
        
        handleApiError(response.error, {
          showAlert: false,
          redirectOn401: true,
          customMessage: 'Failed to fetch jobs information'
        });
      }
    } catch (error) {
     
    } finally {
      
    }
  }, [handleApiError, paginate, getToken]);

  const getJobByIdApi = useCallback(async (id, filters = {}) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call(`jobs/${id}`, 'GET', null, true, false);
   
    if (response.status === 200) {
      
      let jobs = {'data':[response.data.job]}
      setJobs(jobs || []);
      
    } 
  }, [getToken]);

  useEffect(() => {
    
    const handleLoad = async() => {
      const currentPath = window.location.pathname;
      
      if(currentPath.includes(getRoutePath('/jobs/edit/'+id))){
        if (getToken()) {
        
          
          await getJobByIdApi(id);
        }
      }
    };

    // Add onload listener
    window.addEventListener('load', handleLoad);

    // Cleanup listener
    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, [getRoutePath, id, getToken, getJobByIdApi, searchParams,setApi]);

  // Initialize API and load initial data
  useEffect(() => {
    const initializeApi = async () => {
      try {
     
        if (getToken()) {
          let page = parseInt(searchParams.get('page')) || 1;
         
          await getJobs(page);
        }
      } catch (error) {
        hideLoading();
      }
    };
   
    if(initialized){
      initializeApi();
    }
  }, [getToken, getJobs, searchParams, initialized,hideLoading,setApi]);

  const getJobAdditionalData = useCallback(async () => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call('get-job-additional-data', 'GET', null, true);
    if(response.status === 200){
       return response.data;
    } else{
      return handleApiError(response.error, {
        showAlert: false,
        redirectOn401: true,
        customMessage: 'Failed to get job data'
      });
    } 
  }, [getToken, handleApiError]);

  const addJob = async (data) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {

      let formData = new FormData();

      // Append documents (files) - both uploaded and selected documents
      const allDocuments = [
        ...(data.documents || []),
        ...(data.selected_docs || [])
      ];
      
      allDocuments.forEach((item, index) => {
        formData.append(`documents[${index}][id]`, item.id);
        formData.append(`documents[${index}][file]`, item.file || null);
        formData.append(`documents[${index}][name]`, item.name);
        formData.append(`documents[${index}][size]`, item.size || '');
        formData.append(`documents[${index}][type]`, item.type || '');
        formData.append(`documents[${index}][url]`, item.url || '');
        formData.append(`documents[${index}][created_at]`, item.created_at || '');
      });
      
      // Append pictures (files)
      if (data.pictures) {
        data.pictures.forEach((item, index) => {
          formData.append(`pictures[${index}][id]`, item.id);
          formData.append(`pictures[${index}][file]`, item.file);
          formData.append(`pictures[${index}][name]`, item.name);
          formData.append(`pictures[${index}][size]`, item.size);
          formData.append(`pictures[${index}][type]`, item.type);
          formData.append(`pictures[${index}][created_at]`, item.created_at);
         
        });
      }

      // Append receipts from expenses (files)
      let receiptIndex = 0;
      if (data.expenses) {
        data.expenses.forEach((expense, expenseIndex) => {
          if (expense.receipts && Array.isArray(expense.receipts)) {
            expense.receipts.forEach((receipt) => {
              if (receipt.file) {
                formData.append(`receipts[${receiptIndex}][expense_id]`, expense.id);
                formData.append(`receipts[${receiptIndex}][id]`, receipt.id);
                formData.append(`receipts[${receiptIndex}][file]`, receipt.file);
                formData.append(`receipts[${receiptIndex}][name]`, receipt.name);
                formData.append(`receipts[${receiptIndex}][size]`, receipt.size);
                formData.append(`receipts[${receiptIndex}][type]`, receipt.type);
                formData.append(`receipts[${receiptIndex}][created_at]`, receipt.created_at);
                receiptIndex++;
              }
            });
          }
        });
      }


      
      // Remove documents, pictures, and receipts from the original object so we can serialize the rest
      const dataCopy = { ...data };
      delete dataCopy.documents;
      delete dataCopy.pictures;
       
      // Remove receipt file objects from expenses to prevent duplicates
      if (dataCopy.expenses) {
        dataCopy.expenses.forEach((expense) => {
          if (expense.receipts && Array.isArray(expense.receipts)) {
            expense.receipts = expense.receipts.filter(receipt => !receipt.file);
          }
        });
      }
      
      // Append the rest of the data as JSON
      formData.append('data', JSON.stringify(dataCopy));

      const response = await apiInstance.call('jobs', 'POST', formData, true,true);
      
      if (response.status === 201 || response.status === 200) {
        // Refresh the jobs list
        await getJobs();
        return { status: response.status, data: response.data };
      } else {
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors
          };
        } else {
          // Only call handleApiError for non-validation errors
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to add Job'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to create job'
      });
      return { status: 500, error: error.message };
    }
  };

  const getJob = (id) => {
    return jobs?.data?.find(job => Number(job.id) === Number(id));
  };

  const getJobById = useCallback(async (id) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const response = await apiInstance.call(`jobs/${id}`, 'GET', null, true);
      
      if (response.status === 200) {
        return { status: response.status, data: response.data.job };
      } else {
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to fetch job details'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to fetch job details'
      });
      return { status: 500, error: error.message };
    }
  }, [getToken, handleApiError]);

  const updateJob = async (id, updatedData) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      let formData = new FormData();

      // Append documents (files)
      const allDocuments = [
        ...(updatedData.documents || []),
        ...(updatedData.selected_docs || [])
      ];
      
      allDocuments.forEach((item, index) => {
        formData.append(`documents[${index}][id]`, item.id);
        formData.append(`documents[${index}][file]`, item.file);
        formData.append(`documents[${index}][name]`, item.name);
        formData.append(`documents[${index}][size]`, item.size || '');
        formData.append(`documents[${index}][created_at]`, item.created_at || '');
      });

      
      // Append pictures (files)
      if (updatedData.pictures) {
        updatedData.pictures.forEach((item, index) => {
          formData.append(`pictures[${index}][id]`, item.id);
          formData.append(`pictures[${index}][file]`, item.file);
          formData.append(`pictures[${index}][name]`, item.name);
          formData.append(`pictures[${index}][size]`, item.size);
          formData.append(`pictures[${index}][type]`, item.type);
          formData.append(`pictures[${index}][created_at]`, item.created_at);
         
        });
      }

      // Append receipts from expenses (files)
      let receiptIndex = 0;
      if (updatedData.expenses) {
        updatedData.expenses.forEach((expense, expenseIndex) => {
          if (expense.receipts && Array.isArray(expense.receipts)) {
            expense.receipts.forEach((receipt) => {
              if (receipt.file) {
                formData.append(`receipts[${receiptIndex}][expense_id]`, expense.id);
                formData.append(`receipts[${receiptIndex}][id]`, receipt.id);
                formData.append(`receipts[${receiptIndex}][file]`, receipt.file);
                formData.append(`receipts[${receiptIndex}][name]`, receipt.name);
                formData.append(`receipts[${receiptIndex}][size]`, receipt.size);
                formData.append(`receipts[${receiptIndex}][type]`, receipt.type);
                formData.append(`receipts[${receiptIndex}][created_at]`, receipt.created_at);
                receiptIndex++;
              }
            });
          }
        });
      }


      
      // Remove documents, pictures, and receipts from the original object so we can serialize the rest
      const dataCopy = { ...updatedData };
      delete dataCopy.documents;
      delete dataCopy.pictures;
       
      // Remove receipt file objects from expenses to prevent duplicates
      if (dataCopy.expenses) {
        dataCopy.expenses.forEach((expense) => {
          if (expense.receipts && Array.isArray(expense.receipts)) {
            expense.receipts = expense.receipts.filter(receipt => !receipt.file);
          }
        });
      }
      
      // Append the rest of the data as JSON
      formData.append('data', JSON.stringify(dataCopy));
      
      // Now send the request with formData only once
      const response = await apiInstance.call(`jobs/${id}`, 'POST', formData, true, true);
      
      if (response.status === 200) {
        // Refresh the jobs list
        await getJobs();
        return { status: response.status, data: response.data };
      } else {
        if (response.error.status === 422) {
          return {
            status: response.error.status,
             errors: response.error.validationErrors,
          };
        } else {
          // Only call handleApiError for non-validation errors
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to update Job'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to update job'
      });
      return { status: 500, error: error.message };
    }
  };

  const deleteJob = async (id) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const response = await apiInstance.call(`jobs/${id}`, 'DELETE', null, true);
      
      if (response.status === 200) {
        // Refresh the jobs list
        await getJobs();
        navigate(getRoutePath('/jobs'));
        return { status: response.status, data: response.data };
      } else {
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to delete job'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to delete job'
      });
      return { status: 500, error: error.message };
    }
  };

  return (
    <JobContext.Provider
      value={{
        jobs,
        addJob,
        getJob,
        getJobById,
        updateJob,
        deleteJob,
        getJobs,
        getJobAdditionalData,
        formErrors,
        clearErrors
      }}
    >
      {children}
    </JobContext.Provider>
  );
};

export default JobContext; 